import React, { useEffect, useMemo, useState } from "react";
import { Table, Popover, Checkbox, Tooltip, Tag, InputNumber, Input, message, Drawer, Upload, Modal, Select } from "antd";
import { InfoCircleOutlined, PercentageOutlined } from "@ant-design/icons";
import { Icon } from "../../../../components/Component";
import tenderApis from "../../../../api/tender/tenderApis";
import { ColumnsType } from "antd/es/table";
import {
  colors,
  getSupplierInitials,
  formatMYR,
  calculateLowestPrices,
  calculateSummaryData,
  buildMaterialDataSource,
  MaterialInfo,
} from "./comparisonUtils";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as ExcelJS from "exceljs";

interface ComparisonTableProps {
  suppliers: any[];
  evaluationScoreMap: Record<string, any>;
  materialNames: MaterialInfo[];
  selectedMaterials: string[];
  onMaterialSelection: (key: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  isAllSelected: boolean;
  shortlistedData: any[];
  tenderID?: string;
  onDiscountApplied?: () => void;
  targetInputValues?: Record<number, string>;
  onTargetPriceChange?: (tenderMaterialId: number, value: string) => void;
  onTargetPriceBlur?: (tenderMaterialId: number) => void;
  targetPrices?: Record<number, number>;
  statusIndicatorRanges?: { title: string; color: string; startPrice: number; endPrice: number }[];
  // Site hierarchy filter props
  isVillaFlow?: boolean;
  hierarchyFilterOptions?: { towers: string[]; floors: string[]; flats: string[]; villas: string[] };
  selectedTower?: string;
  selectedFloor?: string;
  selectedFlat?: string;
  onTowerChange?: (val: string | undefined) => void;
  onFloorChange?: (val: string | undefined) => void;
  onFlatChange?: (val: string | undefined) => void;
  availableFloors?: string[];
  availableFlats?: string[];
  totalMaterialCount?: number;
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  suppliers,
  evaluationScoreMap,
  materialNames,
  selectedMaterials,
  onMaterialSelection,
  onSelectAll,
  isAllSelected,
  shortlistedData,
  tenderID,
  onDiscountApplied,
  targetInputValues,
  onTargetPriceChange,
  onTargetPriceBlur,
  targetPrices,
  statusIndicatorRanges,
  isVillaFlow,
  hierarchyFilterOptions,
  selectedTower,
  selectedFloor,
  selectedFlat,
  onTowerChange,
  onFloorChange,
  onFlatChange,
  availableFloors,
  availableFlats,
  totalMaterialCount,
}) => {
  const [applyingDiscount, setApplyingDiscount] = useState<Record<string, boolean>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSupplier, setDrawerSupplier] = useState<any>(null);
  const [drawerSupplierIndex, setDrawerSupplierIndex] = useState<number>(0);
  // Line-level discount: { [tenderMaterialId]: discount }
  const [lineDiscounts, setLineDiscounts] = useState<Record<number, number>>({});
  const [revisionReason, setRevisionReason] = useState<string>("");
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  // Controlled pagination for main comparison table
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  // Controlled pagination for drawer table
  const [drawerCurrentPage, setDrawerCurrentPage] = useState<number>(1);
  const [drawerPageSize, setDrawerPageSize] = useState<number>(10);

  const thinBorder = (thickRight = false): Partial<ExcelJS.Borders> => ({
    top: { style: "thin", color: { argb: "FFD9D9D9" } },
    left: { style: "thin", color: { argb: "FFD9D9D9" } },
    bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
    right: thickRight
      ? { style: "medium", color: { argb: "FF595959" } }
      : { style: "thin", color: { argb: "FFD9D9D9" } },
  });

  const handleExport = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("BAFO Analysis");

      const supplierSubHeaders = [
        "Supply Qty",
        "Unit Price (INR)",
        "Variance vs Target",
        "Installation Charges (INR)",
        "Total Price (INR)",
        "Spread vs Lowest",
      ];
      const subColCount = supplierSubHeaders.length;
      const fixedCount = 6; // Tender Material Id, Material Name, Usage, Asked Qty, Need By Date, Target Price

      // Header layout:
      // Row 1: (fixed merged to row 6) | Supplier Name (merged 4 cols)
      // Row 2:                          | Rank #X  |  Score: XX
      // Row 3:                          | Sub Total: xx       | Taxable Amt: xx
      // Row 4:                          | Installation: xx    | Tax Amount: xx
      // Row 5:                          | Discount: xx (x%)   | Gross Amount: xx
      // Row 6: Material Name|Usage|Asked Qty|Need By Date | Supply Qty|Unit Price|Installation|Total Price

      const totalHeaderRows = 6;

      // Add 6 empty rows first
      for (let r = 0; r < totalHeaderRows; r++) {
        ws.addRow([]);
      }

      // --- Fixed headers (merged rows 1-6) ---
      const fixedHeaders = ["Tender Material Id", "Material Name", "Usage", "Asked Qty", "Need By Date", "Target Price"];
      for (let c = 1; c <= fixedCount; c++) {
        ws.mergeCells(1, c, totalHeaderRows, c);
        const cell = ws.getCell(1, c);
        cell.value = fixedHeaders[c - 1];
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFAFAFA" } };
        cell.font = { bold: true, color: { argb: "FF333333" }, size: 11 };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = thinBorder();
      }

      // --- Supplier header rows ---
      suppliers.forEach((supplier, idx) => {
        const startCol = fixedCount + 1 + idx * subColCount;
        const endCol = startCol + subColCount - 1;
        const drawRight = idx < suppliers.length - 1; // thick divider on the right edge
        const bgColor = colors[idx % colors.length].replace("#", "");
        const supplierSum = summaryData.find((s) => s.supplierID === supplier.supplierID);
        const evalData = evaluationScoreMap[supplier.supplierID];

        // Row 1: Supplier Name (merged across 4 cols)
        ws.mergeCells(1, startCol, 1, endCol);
        const nameCell = ws.getCell(1, startCol);
        nameCell.value = supplier.supplierName;
        nameCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${bgColor}` } };
        nameCell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        nameCell.alignment = { horizontal: "center", vertical: "middle" };
        nameCell.border = thinBorder(drawRight);

        // Row 2: Rank & Score (merged across 4 cols)
        ws.mergeCells(2, startCol, 2, endCol);
        const rankCell = ws.getCell(2, startCol);
        if (evalData) {
          rankCell.value = `Rank #${evalData.rank}    Score: ${evalData.overallScore.toFixed(1)}`;
        }
        rankCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F8FA" } };
        rankCell.font = { size: 10, color: { argb: "FF333333" } };
        rankCell.alignment = { horizontal: "center", vertical: "middle" };
        rankCell.border = thinBorder(drawRight);

        if (supplierSum) {
          // Row 3: Sub Total | Taxable Amt
          ws.mergeCells(3, startCol, 3, startCol + 2);
          ws.mergeCells(3, startCol + 3, 3, endCol);
          const subTotalCell = ws.getCell(3, startCol);
          subTotalCell.value = `Sub Total: ${formatMYR(supplierSum.subTotal)}`;
          subTotalCell.font = { size: 10, bold: true, color: { argb: "FF333333" } };
          subTotalCell.alignment = { horizontal: "center", vertical: "middle" };
          subTotalCell.border = thinBorder();
          const taxableCell = ws.getCell(3, startCol + 3);
          taxableCell.value = `Taxable Amt: ${formatMYR(supplierSum.taxableAmount)}`;
          taxableCell.font = { size: 10, bold: true, color: { argb: "FF333333" } };
          taxableCell.alignment = { horizontal: "center", vertical: "middle" };
          taxableCell.border = thinBorder(drawRight);

          // Row 4: Installation | Tax Amount
          ws.mergeCells(4, startCol, 4, startCol + 2);
          ws.mergeCells(4, startCol + 3, 4, endCol);
          const installCell = ws.getCell(4, startCol);
          installCell.value = `Installation: ${formatMYR(supplierSum.totalInstallationCharges)}`;
          installCell.font = { size: 10, color: { argb: "FF666666" } };
          installCell.alignment = { horizontal: "center", vertical: "middle" };
          installCell.border = thinBorder();
          const taxAmtCell = ws.getCell(4, startCol + 3);
          taxAmtCell.value = `Tax Amount: ${formatMYR(supplierSum.taxes)}`;
          taxAmtCell.font = { size: 10, color: { argb: "FF666666" } };
          taxAmtCell.alignment = { horizontal: "center", vertical: "middle" };
          taxAmtCell.border = thinBorder(drawRight);

          // Row 5: Discount (red) | Gross Amount (blue)
          ws.mergeCells(5, startCol, 5, startCol + 2);
          ws.mergeCells(5, startCol + 3, 5, endCol);
          const discountCell = ws.getCell(5, startCol);
          discountCell.value = `Discount: ${formatMYR(supplierSum.totalDiscount)} (${supplierSum.discountPercentage}%)`;
          discountCell.font = { size: 10, bold: true, color: { argb: "FFFF0000" } };
          discountCell.alignment = { horizontal: "center", vertical: "middle" };
          discountCell.border = thinBorder();
          const grossCell = ws.getCell(5, startCol + 3);
          grossCell.value = `Gross Amount: ${formatMYR(supplierSum.grossAmount)}`;
          grossCell.font = { size: 10, bold: true, color: { argb: "FF1890FF" } };
          grossCell.alignment = { horizontal: "center", vertical: "middle" };
          grossCell.border = thinBorder(drawRight);
        } else {
          // Merge rows 3-5 if no summary
          for (let r = 3; r <= 5; r++) {
            ws.mergeCells(r, startCol, r, endCol);
            ws.getCell(r, startCol).border = thinBorder(drawRight);
          }
        }

        // Row 6: Sub-headers
        for (let s = 0; s < subColCount; s++) {
          const cell = ws.getCell(totalHeaderRows, startCol + s);
          cell.value = supplierSubHeaders[s];
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
          cell.font = { bold: true, color: { argb: "FF333333" }, size: 10 };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.border = thinBorder(drawRight && s === subColCount - 1);
        }
      });

      // Set column widths
      ws.getColumn(1).width = 20;
      ws.getColumn(2).width = 35;
      ws.getColumn(3).width = 15;
      ws.getColumn(4).width = 12;
      ws.getColumn(5).width = 15;
      ws.getColumn(6).width = 18;
      suppliers.forEach((_, idx) => {
        const base = fixedCount + 1 + idx * subColCount;
        ws.getColumn(base).width = 12;       // Supply Qty
        ws.getColumn(base + 1).width = 18;   // Unit Price
        ws.getColumn(base + 2).width = 22;   // Variance vs Target
        ws.getColumn(base + 3).width = 24;   // Installation Charges
        ws.getColumn(base + 4).width = 20;   // Total Price
        ws.getColumn(base + 5).width = 22;   // Spread vs Lowest
      });

      // Row heights for header
      ws.getRow(1).height = 22;
      ws.getRow(2).height = 18;
      ws.getRow(6).height = 20;

      // --- Data rows ---
      dataSource.forEach((row: any) => {
        // Build material name with site hierarchy
        const parts = [
          row.unitName || "",
          row.floorNo ? `Floor: ${row.floorNo}` : "",
          row.subUnitName ? `Flat: ${row.subUnitName}` : "",
        ].filter(Boolean);
        const materialLabel = parts.length > 0
          ? `${row.materialName}\n${parts.join(" / ")}`
          : row.materialName;

        const tpValue = targetInputValues?.[row.tenderMaterialId]
          ? Number(targetInputValues[row.tenderMaterialId])
          : "";
        const askedQtyDisplay = row.uom
          ? `${row.askedQty || 0} ${row.uom}`
          : row.askedQty || 0;
        const rowData: any[] = [
          row.tenderMaterialId,
          materialLabel,
          row.usage || "-",
          askedQtyDisplay,
          row.needByDate ? dayjs(row.needByDate).format("DD-MMM-YYYY") : "-",
          tpValue,
        ];

        const tpNum = typeof tpValue === "number" ? tpValue : 0;
        const lowestForRow = lowestPricePerMaterial[row.tenderMaterialId];

        suppliers.forEach((s) => {
          const val = row[s.supplierID];
          if (val) {
            // Variance vs Target (unit price - target price)
            let varianceCell: string = "-";
            if (tpNum > 0) {
              const variance = val.unitPrice - tpNum;
              const deviation = (variance / tpNum) * 100;
              const sign = variance > 0 ? "+" : "";
              varianceCell = `${sign}${formatMYR(variance)} (${deviation.toFixed(1)}%)`;
            }

            // Spread vs Lowest (total price - lowest total price)
            let spreadCell: string = "-";
            if (lowestForRow && lowestForRow > 0) {
              const isLowest = val.totalPrice === lowestForRow;
              if (!isLowest) {
                const spreadAmt = val.totalPrice - lowestForRow;
                const spreadPct = (spreadAmt / lowestForRow) * 100;
                spreadCell = `+${formatMYR(spreadAmt)} (${spreadPct.toFixed(1)}%)`;
              }
            }

            rowData.push(
              val.supplyQty,
              formatMYR(val.unitPrice),
              varianceCell,
              formatMYR(val.totalInstallationCharges),
              formatMYR(val.totalPrice),
              spreadCell
            );
          } else {
            rowData.push("-", "-", "-", "-", "-", "-");
          }
        });

        const excelRow = ws.addRow(rowData);
        if (parts.length > 0) excelRow.height = 32;

        // Style fixed cols
        for (let c = 1; c <= fixedCount; c++) {
          const cell = excelRow.getCell(c);
          cell.border = thinBorder();
          cell.alignment = { vertical: "middle", wrapText: true };
          if (c === 1) {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          }
          if (c === 2) {
            cell.font = { bold: true, size: 10 };
          }
          if (c === 4 || c === 5) cell.alignment = { horizontal: "center", vertical: "middle" };
        }

        // Style supplier data cells
        suppliers.forEach((s, idx) => {
          const base = fixedCount + 1 + idx * subColCount;
          const drawRight = idx < suppliers.length - 1;
          const val = row[s.supplierID];
          const isLowest = val && val.totalPrice === lowestPricePerMaterial[row.tenderMaterialId];
          const isHighest = val && val.totalPrice === highestPricePerMaterial[row.tenderMaterialId] && !isLowest;
          const variance = val && tpNum > 0 ? val.unitPrice - tpNum : 0;

          for (let sc = 0; sc < subColCount; sc++) {
            const cell = excelRow.getCell(base + sc);
            cell.border = thinBorder(drawRight && sc === subColCount - 1);
            cell.alignment = { horizontal: sc === 0 ? "center" : "right", vertical: "middle" };

            // sc index map: 0=Qty, 1=UnitPrice, 2=Variance, 3=Install, 4=TotalPrice, 5=Spread
            // Highlight lowest total price in green, highest in red
            if (sc === 4 && isLowest) {
              cell.font = { bold: true, color: { argb: "FF52C41A" }, size: 10 };
            } else if (sc === 4 && isHighest) {
              cell.font = { bold: true, color: { argb: "FFFF4D4F" }, size: 10 };
            }

            // Variance: red if above target, green if at/below target
            if (sc === 2 && val && tpNum > 0) {
              cell.font = {
                bold: true,
                color: { argb: variance > 0 ? "FFFF4D4F" : "FF52C41A" },
                size: 10,
              };
            }

            // Spread: red if not lowest (always positive when shown)
            if (sc === 5 && val && !isLowest && lowestPricePerMaterial[row.tenderMaterialId]) {
              cell.font = { bold: true, color: { argb: "FFFF4D4F" }, size: 10 };
            }
          }
        });
      });

      // --- Summary row ---
      const summaryRowData: any[] = ["", "TOTAL", "", "", "", ""];
      suppliers.forEach((s) => {
        const summary = summaryData.find((sd) => sd.supplierID === s.supplierID);
        if (summary) {
          summaryRowData.push(
            "",
            formatMYR(summary.subTotal),
            "",
            formatMYR(summary.totalInstallationCharges),
            formatMYR(summary.grossAmount),
            ""
          );
        } else {
          summaryRowData.push("", "", "", "", "", "");
        }
      });
      const totalRow = ws.addRow(summaryRowData);
      // Columns that sit at the right edge of a non-last supplier block — these get the thick divider.
      const blockEndCols = new Set<number>();
      for (let i = 0; i < suppliers.length - 1; i++) {
        blockEndCols.add(fixedCount + (i + 1) * subColCount);
      }
      totalRow.eachCell((cell, colNumber) => {
        cell.border = thinBorder(blockEndCols.has(colNumber));
        cell.font = { bold: true, size: 10 };
        if (colNumber > fixedCount) {
          cell.alignment = { horizontal: "right", vertical: "middle" };
        }
      });
      totalRow.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };

      // --- Target Prices sheet (for bulk import) ---
      const tpSheet = wb.addWorksheet("Target Prices");
      tpSheet.columns = [
        { header: "Tender Material Id", key: "tenderMaterialId", width: 20 },
        { header: "Material Name", key: "materialName", width: 40 },
        { header: "Target Price", key: "targetPrice", width: 20 },
      ];
      const tpHeaderRow = tpSheet.getRow(1);
      tpHeaderRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = thinBorder();
      });
      materialNames.forEach((mat) => {
        const row = tpSheet.addRow({
          tenderMaterialId: mat.tenderMaterialId,
          materialName: mat.materialName,
          targetPrice: targetInputValues?.[mat.tenderMaterialId] ? Number(targetInputValues[mat.tenderMaterialId]) : "",
        });
        row.eachCell((cell) => {
          cell.border = thinBorder();
        });
      });

      // Generate and save
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BAFO_Analysis_Comparison.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      message.success("Exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export");
    }
  };

  const openDiscountDrawer = (supplier: any, index: number) => {
    setDrawerSupplier(supplier);
    setDrawerSupplierIndex(index);
    setLineDiscounts({});
    setRevisionReason("");
    setDrawerOpen(true);
  };

  const getDrawerMaterials = () => {
    if (!drawerSupplier) return [];
    return materialNames.map((mat, idx) => {
      const lineItem = (drawerSupplier.lineItems || []).find(
        (li: any) => li.tenderMaterialId === mat.tenderMaterialId
      );
      if (!lineItem) return null;
      const totalPrice = lineItem.quotedQuantity * lineItem.quotedUnitPrice + (lineItem.totalInstallationCharges || 0);
      return {
        key: String(idx),
        materialName: mat.materialName,
        unitName: mat.unitName || "",
        floorNo: mat.floorNo || "",
        subUnitName: mat.subUnitName || "",
        locationName: mat.locationName || "",
        usage: lineItem.usage || "-",
        supplyQty: lineItem.quotedQuantity,
        unitPrice: lineItem.quotedUnitPrice,
        totalInstallationCharges: lineItem.totalInstallationCharges || 0,
        totalPrice,
        tenderMaterialId: mat.tenderMaterialId,
        lineItemId: lineItem.bidMaterialId || lineItem.supplierBidMaterialId || lineItem.id || lineItem.lineItemId || lineItem.tenderMaterialId,
      };
    }).filter(Boolean) as any[];
  };

  const handleDrawerExport = async () => {
    if (!drawerSupplier) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(drawerSupplier.supplierName);
      const headers = [
        "Bid Material Id",
        "Name",
        "Material",
        "Usage",
        "Supplier Qty",
        "Unit Price",
        "Installation Charges",
        "Basic Price (Total Price)",
        "Discount %",
      ];
      const headerRow = ws.addRow(headers);
      const bgColor = colors[drawerSupplierIndex % colors.length].replace("#", "");
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${bgColor}` } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = thinBorder();
      });
      ws.getColumn(1).width = 18;
      ws.getColumn(2).width = 40;
      ws.getColumn(3).width = 30;
      ws.getColumn(4).width = 16;
      ws.getColumn(5).width = 14;
      ws.getColumn(6).width = 16;
      ws.getColumn(7).width = 20;
      ws.getColumn(8).width = 22;
      ws.getColumn(9).width = 14;

      const materials = getDrawerMaterials();
      materials.forEach((mat: any) => {
        // Build site hierarchy line below material name
        const hierarchyParts = [
          mat.unitName,
          mat.floorNo ? `Floor: ${mat.floorNo}` : "",
          mat.subUnitName ? `Flat: ${mat.subUnitName}` : "",
          mat.locationName ? `Location: ${mat.locationName}` : "",
        ].filter(Boolean);
        const nameLabel = hierarchyParts.length > 0
          ? `${mat.materialName}\n${hierarchyParts.join(" / ")}`
          : mat.materialName;

        const disc = lineDiscounts[mat.tenderMaterialId] || 0;
        const row = ws.addRow([
          mat.lineItemId,
          nameLabel,
          mat.materialName,
          mat.usage || "",
          mat.supplyQty,
          mat.unitPrice,
          mat.totalInstallationCharges,
          mat.totalPrice,
          disc || "",
        ]);
        row.eachCell((cell, colNumber) => {
          cell.border = thinBorder();
          cell.alignment = { vertical: "middle", wrapText: colNumber === 2 };
          if (colNumber === 2) cell.font = { bold: true, size: 10 };
          if ([5, 6, 7, 8].includes(colNumber)) cell.alignment = { horizontal: "right", vertical: "middle" };
          if (colNumber === 1 || colNumber === 9) cell.alignment = { horizontal: "center", vertical: "middle" };
        });
        // Highlight discount column
        const discCell = row.getCell(9);
        discCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFBE6" } };
        if (hierarchyParts.length > 0) row.height = 32;
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${drawerSupplier.supplierName}_Discount.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success("Exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export");
    }
  };

  const handleDrawerImport = (file: any) => {
    const processImport = async () => {
      try {
        if (!drawerSupplier) {
          message.error("No supplier selected");
          return;
        }

        // Validate Excel format before sending to API
        const buffer = await file.arrayBuffer();
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buffer);
        const ws = wb.getWorksheet(1);
        if (!ws) {
          message.error("No worksheet found in the file");
          return;
        }
        const headerRow = ws.getRow(1);
        const firstHeader = String(headerRow.getCell(1).value || "").trim().toLowerCase();

        // Detect old format (starts with "Material Name" instead of "Bid Material Id")
        if (firstHeader === "material name") {
          message.error("This Excel uses an old format. Please re-export the discount file and try again.");
          return;
        }

        // Create a new File from the buffer for the API call
        const uploadFile = new File([buffer], file.name, { type: file.type });
        const bidId = drawerSupplier.bidId || drawerSupplier.supplierBidId || drawerSupplier.supplierID;
        const formData = new FormData();
        formData.append("files", uploadFile);
        formData.append("tenderId", String(tenderID));
        formData.append("bidId", String(bidId));
        formData.append("bidType", "supplier");

        const response = await tenderApis.validateDiscountBulkUpload(formData);
        const responseData = response?.data || response || {};
        const rows = responseData?.rows || responseData || [];

        if (responseData?.status === "VALIDATION_ERRORS") {
          const errorMessages = rows
            .filter((row: any) => row.errors)
            .map((row: any) => {
              const parsed = JSON.parse(row.jsonData || "{}");
              return `${parsed.lineName || "Unknown"}: ${row.errors}`;
            });
          if (errorMessages.length > 0) {
            message.error(`Validation errors: ${errorMessages.join("; ")}`);
            return;
          }
        }

        const materials = getDrawerMaterials();
        const newDiscounts: Record<number, number> = { ...lineDiscounts };
        let importedCount = 0;

        rows.forEach((row: any) => {
          const parsed = typeof row.jsonData === "string" ? JSON.parse(row.jsonData) : row;
          if (parsed.discountPercentage > 0 && parsed.lineId > 0 && !row.errors) {
            const matchedMat = materials.find(
              (m: any) => m.lineItemId === parsed.lineId
            );
            if (matchedMat) {
              newDiscounts[(matchedMat as any).tenderMaterialId] = parsed.discountPercentage;
              importedCount++;
            }
          }
        });

        setLineDiscounts(newDiscounts);
        message.success(`Imported discounts for ${importedCount} materials`);
      } catch (error) {
        console.error("Import error:", error);
        message.error("Failed to validate and import discount file");
      }
    };
    processImport();
    return false;
  };

  const handleImportTargetPrice = (file: any) => {
    const processImport = async () => {
      try {
        const buffer = await file.arrayBuffer();
        const uploadWb = new ExcelJS.Workbook();
        await uploadWb.xlsx.load(buffer);

        const getCellNumericValue = (cell: ExcelJS.Cell): number => {
          const val = cell.value;
          if (val == null || val === "") return 0;
          if (typeof val === "number") return val;
          if (typeof val === "string") return Number(val) || 0;
          if (typeof val === "object" && "result" in val) return Number((val as any).result) || 0;
          return Number(val) || 0;
        };

        const getCellStringValue = (cell: ExcelJS.Cell): string => {
          const val = cell.value;
          if (val == null) return "";
          if (typeof val === "string") return val;
          if (typeof val === "object" && "result" in val) return String((val as any).result || "");
          if (typeof val === "object" && "richText" in val) {
            return ((val as any).richText || []).map((rt: any) => rt.text).join("");
          }
          return String(val);
        };

        // Build a clean single-sheet Excel with only the target price data
        const cleanWb = new ExcelJS.Workbook();
        const cleanWs = cleanWb.addWorksheet("Target Prices");
        cleanWs.addRow(["Tender Material Id", "Material Name", "Target Price"]);

        let hasData = false;

        // Strategy 1: Look for "Target Prices" sheet with valid TP data
        let tpSheet: ExcelJS.Worksheet | undefined;
        uploadWb.eachSheet((worksheet) => {
          if (worksheet.name.toLowerCase().trim() === "target prices") {
            tpSheet = worksheet;
          }
        });
        if (!tpSheet) {
          const firstSheet = uploadWb.worksheets[0];
          if (firstSheet) {
            const firstHeader = String(firstSheet.getRow(1).getCell(1).value || "").trim().toLowerCase();
            if (firstHeader === "tender material id") {
              tpSheet = firstSheet;
            }
          }
        }

        if (tpSheet) {
          tpSheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            const id = getCellNumericValue(row.getCell(1));
            const name = getCellStringValue(row.getCell(2));
            const tp = getCellNumericValue(row.getCell(3));
            if (id > 0 && tp > 0) {
              cleanWs.addRow([id, name, tp]);
              hasData = true;
            }
          });
        }

        // Strategy 2: If no TP data found, read from BAFO Analysis sheet
        // (user may have filled Target Price column E in the main sheet)
        if (!hasData) {
          const bafoSheet = uploadWb.worksheets[0];
          if (bafoSheet) {
            // BAFO sheet has 6 header rows, data starts at row 7
            // Columns: A=Tender Material Id, B=Material Name, C=Usage, D=Asked Qty, E=Need By Date, F=Target Price
            const totalHeaderRows = 6;
            bafoSheet.eachRow((row, rowNumber) => {
              if (rowNumber <= totalHeaderRows) return;
              const matName = getCellStringValue(row.getCell(2)).split("\n")[0].trim();
              const tp = getCellNumericValue(row.getCell(6));
              if (!matName || matName === "TOTAL" || tp <= 0) return;

              const matchedMat = materialNames.find(
                (m) => m.materialName.trim().toLowerCase() === matName.toLowerCase()
              );
              if (matchedMat) {
                cleanWs.addRow([matchedMat.tenderMaterialId, matchedMat.materialName, tp]);
                hasData = true;
              }
            });
          }
        }

        if (!hasData) {
          message.error("No valid target price data found. Please fill in the Target Price column in the exported file.");
          return;
        }

        const cleanBuffer = await cleanWb.xlsx.writeBuffer();
        const uploadFile = new File([cleanBuffer], file.name, { type: file.type });

        const formData = new FormData();
        formData.append("files", uploadFile);
        formData.append("tenderId", String(tenderID));

        const response = await tenderApis.validateTargetPriceBulkUpload(formData);
        const responseData = response?.data || response || {};
        const rows = responseData?.rows || responseData || [];

        if (responseData?.status === "VALIDATION_ERRORS") {
          const errorMessages = rows
            .filter((row: any) => row.errors)
            .map((row: any) => {
              const parsed = typeof row.jsonData === "string" ? JSON.parse(row.jsonData) : row;
              return `${parsed.lineName || "Unknown"}: ${row.errors}`;
            });
          if (errorMessages.length > 0) {
            message.error(`Validation errors: ${errorMessages.join("; ")}`);
            return;
          }
        }

        let importedCount = 0;
        rows.forEach((row: any) => {
          const parsed = typeof row.jsonData === "string" ? JSON.parse(row.jsonData) : row;
          if (parsed.targetPrice >= 0 && parsed.lineId > 0 && !row.errors) {
            const matchedMaterial = materialNames.find(
              (m) => m.tenderMaterialId === parsed.lineId
            );
            if (matchedMaterial) {
              onTargetPriceChange?.(matchedMaterial.tenderMaterialId, String(parsed.targetPrice));
              importedCount++;
            }
          }
        });

        if (importedCount > 0) {
          message.success(`Imported target prices for ${importedCount} material(s).`);
        } else {
          message.warning("No matching materials found in the uploaded file.");
        }
      } catch (error) {
        console.error("Target price import error:", error);
        message.error("Failed to import target prices from file.");
      }
    };
    processImport();
    return false;
  };

  const handleApplyClick = () => {
    if (!drawerSupplier) return;
    const entries = Object.entries(lineDiscounts).filter(
      ([_, val]) => val > 0
    );
    if (entries.length === 0) {
      message.warning("Please enter discount % for at least one material.");
      return;
    }
    setReasonModalOpen(true);
  };

  const handleApplyAllDiscounts = async () => {
    if (!drawerSupplier) return;
    if (!revisionReason.trim()) {
      message.warning("Please enter a reason for the discount.");
      return;
    }
    const entries = Object.entries(lineDiscounts).filter(
      ([_, val]) => val > 0
    );

    try {
      setReasonModalOpen(false);
      setApplyingDiscount((prev) => ({ ...prev, [drawerSupplier.supplierID]: true }));
      const bidId = drawerSupplier.bidId || drawerSupplier.supplierBidId || drawerSupplier.supplierID;

      const materials = getDrawerMaterials();
      const materialDiscounts = entries.map(([matId, val]) => {
        const mat = materials.find((m: any) => m.tenderMaterialId === Number(matId));
        return {
          bidMaterialId: mat?.lineItemId,
          discountPercent: val,
        };
      });

      await tenderApis.applyDiscount(Number(tenderID), bidId, {
        materialDiscounts,
        revisionReason: revisionReason.trim(),
      });

      message.success("Discounts applied successfully!");
      setLineDiscounts({});
      setRevisionReason("");
      setDrawerOpen(false);
      onDiscountApplied?.();
    } catch (error) {
      console.error("Error applying discounts:", error);
      message.error("Failed to apply discounts");
    } finally {
      setApplyingDiscount((prev) => ({ ...prev, [drawerSupplier.supplierID]: false }));
    }
  };

  const dataSource = useMemo(
    () => buildMaterialDataSource(materialNames, suppliers, shortlistedData),
    [materialNames, suppliers, shortlistedData]
  );

  // Reset to page 1 when the underlying data shrinks below the current page window
  // (e.g. filters applied) so the table never lands on an empty page.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(dataSource.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [dataSource.length, pageSize, currentPage]);

  // Reset drawer pagination when drawer opens or supplier switches
  useEffect(() => {
    setDrawerCurrentPage(1);
  }, [drawerOpen, drawerSupplier?.supplierID]);

  const lowestPricePerMaterial = useMemo(
    () => calculateLowestPrices(materialNames, suppliers),
    [materialNames, suppliers]
  );

  const highestPricePerMaterial = useMemo(() => {
    const result: Record<number, number> = {};
    materialNames.forEach((mat) => {
      let maxPrice = -Infinity;
      suppliers.forEach((s) => {
        const lineItem = (s.lineItems || []).find(
          (li: any) => li.tenderMaterialId === mat.tenderMaterialId
        );
        if (lineItem) {
          const total = lineItem.quotedQuantity * lineItem.quotedUnitPrice + (lineItem.totalInstallationCharges || 0) - (lineItem.totalDiscount || 0);
          if (total > maxPrice) maxPrice = total;
        }
      });
      if (maxPrice > -Infinity) result[mat.tenderMaterialId] = maxPrice;
    });
    return result;
  }, [materialNames, suppliers]);

  const summaryData = useMemo(
    () => calculateSummaryData(suppliers, materialNames),
    [suppliers, materialNames]
  );

  if (suppliers.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#999",
          background: "#fafafa",
          borderRadius: 8,
          margin: "12px 0",
        }}
      >
        Select suppliers from the cards above to compare
      </div>
    );
  }

  const renderUsageCell = (text: any, maxLength: number) => {
    const val = text || "-";
    if (val !== "-" && val.length > maxLength) {
      return (
        <Tooltip title={val}>
          <span style={{ cursor: "pointer" }}>
            {val.slice(0, maxLength)}…{" "}
            <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
          </span>
        </Tooltip>
      );
    }
    return val;
  };

  const columns: ColumnsType<any> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
        />
      ),
      key: "selection",
      fixed: "left",
      width: 30,
      render: (_: any, record: any) => {
        const isDisabled = record.remainingQty === 0;
        return (
          <Tooltip title={isDisabled ? "Remaining quantity is 0" : ""}>
            <Checkbox
              checked={selectedMaterials.includes(record.key)}
              disabled={isDisabled}
              onChange={(e) =>
                onMaterialSelection(record.key, e.target.checked)
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      fixed: "left",
      width: 220,
      render: (text: string, record: any) => {
        const parts = [
          record.unitName || "",
          record.floorNo ? `Floor: ${record.floorNo}` : "",
          record.subUnitName ? `Flat: ${record.subUnitName}` : "",
        ].filter(Boolean);
        return (
          <div>
            <b>{text}</b>
            {parts.length > 0 && (
              <div style={{ fontSize: 12, color: "#1890ff", fontWeight: 500, marginTop: 3 }}>
                {parts.join(" / ")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Usage",
      dataIndex: "usage",
      key: "usage",
      fixed: "left",
      width: 150,
      ellipsis: true,
      render: (text: any) => renderUsageCell(text, 20),
    },
    {
      title: "Asked Qty",
      dataIndex: "askedQty",
      key: "askedQty",
      fixed: "left",
      width: 90,
      align: "center",
      render: (qty: number, record: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <span>{qty}</span>
          <Popover
            content={
              <div style={{ maxWidth: 620, maxHeight: 350, overflow: "auto" }}>
                <div style={{ marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: "#222", fontWeight: 600 }}>Material:</span>{" "}
                  <span style={{ color: "#333" }}>{record.materialName}</span>
                </div>
                <div style={{ marginBottom: 10, fontSize: 13 }}>
                  <span style={{ color: "#222", fontWeight: 600 }}>UOM:</span>{" "}
                  <span style={{ color: "#333" }}>{record.uom}</span>
                </div>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: "#222", display: "flex", alignItems: "center", gap: 6 }}>
                  <CalendarOutlined style={{ color: "#1890ff" }} /> Production & Delivery Schedules
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", minWidth: 550 }}>
                    <thead>
                      <tr style={{ background: "#f7f8fa" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Supplier</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Production Plan</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Production Schedule</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#333", fontWeight: 600, borderBottom: "2px solid #e8e8e8", whiteSpace: "nowrap" }}>Shipment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.map((s: any) => {
                        const li = (s.lineItems || []).find((l: any) => l.tenderMaterialId === record.tenderMaterialId);
                        if (!li) return null;
                        const plan = li.planningSchedule;
                        const prod = li.productionSchedule;
                        const ship = li.shipmentSchedule;
                        return (
                          <tr key={s.supplierID} style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <td style={{ padding: "6px 8px", fontWeight: 600, color: "#222", whiteSpace: "nowrap" }}>{s.supplierName}</td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {plan ? (
                                <>
                                  {dayjs(plan.startDate).format("DD-MMM-YY")}
                                  <span style={{ color: "#1890ff", margin: "0 3px" }}>&rarr;</span>
                                  {dayjs(plan.endDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 4, fontSize: 10, color: "#1890ff", fontWeight: 500 }}>({plan.leadDays}d)</span>
                                </>
                              ) : "-"}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {prod ? (
                                <>
                                  {dayjs(prod.plannedStartDate).format("DD-MMM-YY")}
                                  <span style={{ color: "#1890ff", margin: "0 3px" }}>&rarr;</span>
                                  {dayjs(prod.plannedEndDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 4, fontSize: 10, color: "#52c41a", fontWeight: 500 }}>({prod.quantity} units)</span>
                                </>
                              ) : "-"}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {ship ? (
                                <>
                                  {dayjs(ship.shippingDate).format("DD-MMM-YY")}
                                  <span style={{ marginLeft: 6, color: "#fa541c", fontWeight: 600, fontSize: 11 }}>ETA: {dayjs(ship.eta).format("DD-MMM-YY")}</span>
                                </>
                              ) : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            }
            title={null}
            placement="bottom"
            overlayStyle={{ maxWidth: 650 }}
            overlayInnerStyle={{ padding: 12 }}
            trigger="hover"
          >
            <InfoCircleOutlined
              style={{ color: "#1890ff", cursor: "pointer" }}
            />
          </Popover>
        </div>
      ),
    },
    {
      title: "Need By Date",
      dataIndex: "needByDate",
      key: "needByDate",
      fixed: "left",
      width: 100,
      align: "center",
      render: (date: string) => date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Target Price",
      dataIndex: "tenderMaterialId",
      key: "targetPrice",
      fixed: "left",
      width: 100,
      render: (_: any, record: any) => (
        <InputNumber
          size="small"
          style={{ width: "100%" }}
          min={0}
          placeholder="Enter TP"
          value={targetInputValues?.[record.tenderMaterialId] != null ? Number(targetInputValues[record.tenderMaterialId]) : undefined}
          onChange={(val) => onTargetPriceChange?.(record.tenderMaterialId, String(val ?? ""))}
          onBlur={() => onTargetPriceBlur?.(record.tenderMaterialId)}
        />
      ),
    },
  ];

  // Dynamic supplier columns
  suppliers.forEach((supplier, index) => {
    const evalData = evaluationScoreMap[supplier.supplierID];
    const supplierSummary = summaryData.find(
      (s) => s.supplierID === supplier.supplierID
    );
    columns.push({
      title: (
        <div>
          {/* Top — Avatar + Discount button */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", marginBottom: 6, position: "relative" }}>
            <Popover
              content={
                <div>
                  <div><b>Supplier:</b> {supplier.supplierName}</div>
                  <div><b>ID:</b> {supplier.supplierID}</div>
                  <div><b>Payment Terms:</b> {supplier.paymentTerms}</div>
                  <div><b>Quote Valid:</b> {supplier.quoteValidityDate}</div>
                  {evalData && (
                    <>
                      <hr style={{ margin: "6px 0" }} />
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Evaluation Scores</div>
                      {evalData.sections.map((s: any) => (
                        <div key={s.name} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <span>{s.name}:</span>
                          <span>{s.score}/{s.maxScore}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              }
              title="Supplier Info"
              placement="top"
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: colors[index % colors.length],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {getSupplierInitials(supplier.supplierName)}
              </div>
            </Popover>
            {/* Discount button — top right corner */}
            {supplierSummary && (
              <div
                className="btn btn-white btn-outline-light"
                onClick={() => openDiscountDrawer(supplier, index)}
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  cursor: "pointer",
                }}
              >
                <PercentageOutlined style={{ marginRight: 4 }} />
                <span>Discount</span>
              </div>
            )}
          </div>
          {/* Supplier name */}
          <div style={{ textAlign: "center", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {supplier.supplierName}
          </div>
          {evalData && (
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 6, flexWrap: "wrap" }}>
              <Tag
                color={evalData.rank === 1 ? "gold" : evalData.rank === 2 ? "default" : evalData.rank === 3 ? "orange" : "blue"}
                style={{ fontSize: 11, margin: 0 }}
              >
                Rank #{evalData.rank}
              </Tag>
              <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                Score: {evalData.overallScore.toFixed(1)}
              </Tag>
            </div>
          )}
          {/* Bottom — Two columns: Details left, Totals right */}
          {supplierSummary && (
            <div style={{ display: "flex", gap: 12, fontSize: 11, whiteSpace: "nowrap", borderTop: "1px solid #f0f0f0", paddingTop: 6 }}>
              <div style={{ flex: 1 }}>
                <div><span style={{ color: "#666" }}>Sub Total: </span><b>{formatMYR(supplierSummary.subTotal)}</b></div>
                <div><span style={{ color: "#666" }}>Installation: </span><b>{formatMYR(supplierSummary.totalInstallationCharges)}</b></div>
                <div style={{ color: "red" }}><span>Discount: </span><b>{formatMYR(supplierSummary.totalDiscount)} ({supplierSummary.discountPercentage}%)</b></div>
              </div>
              <div style={{ flex: 1, borderLeft: "1px solid #f0f0f0", paddingLeft: 10 }}>
                <div><span style={{ color: "#666" }}>Taxable Amt: </span><b>{formatMYR(supplierSummary.taxableAmount)}</b></div>
                <div><span style={{ color: "#666" }}>Tax Amount: </span><b>{formatMYR(supplierSummary.taxes)}</b></div>
                <div>
                  <span style={{ color: "#1890ff", fontWeight: 600 }}>Gross Amount: </span>
                  <b style={{ color: "#1890ff" }}>{formatMYR(supplierSummary.grossAmount)}</b>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
      key: supplier.supplierID,
      children: [
        {
          title: "Supply Qty",
          dataIndex: supplier.supplierID,
          key: `${supplier.supplierID}_qty`,
          width: 100,
          align: "center" as const,
          render: (val: any) => (val ? val.supplyQty : "-"),
        },
        {
          title: "Unit Price (INR)",
          dataIndex: supplier.supplierID,
          key: `${supplier.supplierID}_unit`,
          width: 130,
          align: "right" as const,
          render: (val: any, record: any) => {
            if (!val) return "-";
            const tp = targetPrices?.[record.tenderMaterialId];
            let indicatorColor: string | undefined;
            let indicatorTitle: string | undefined;
            let deviation = 0;
            if (tp && tp > 0 && statusIndicatorRanges && statusIndicatorRanges.length > 0) {
              deviation = ((val.unitPrice - tp) / tp) * 100;
              if (deviation > 0) {
                // Sort indicators by startPrice to ensure correct matching order
                const sortedIndicators = [...statusIndicatorRanges].sort(
                  (a, b) => a.startPrice - b.startPrice
                );
                for (const ind of sortedIndicators) {
                  if (ind.endPrice > 0) {
                    // Range indicator: startPrice <= deviation <= endPrice
                    if (deviation >= ind.startPrice && deviation <= ind.endPrice) {
                      indicatorColor = ind.color;
                      indicatorTitle = ind.title;
                      break;
                    }
                  } else {
                    // Open-ended (Outlier): deviation >= startPrice
                    if (deviation >= ind.startPrice) {
                      indicatorColor = ind.color;
                      indicatorTitle = ind.title;
                      break;
                    }
                  }
                }
              }
            }
            const variance = tp && tp > 0 ? val.unitPrice - tp : 0;
            const priceEl = (
              <span
                style={{
                  backgroundColor: indicatorColor,
                  padding: indicatorColor ? "2px 6px" : undefined,
                  borderRadius: indicatorColor ? 4 : undefined,
                  fontWeight: indicatorColor ? 600 : undefined,
                }}
              >
                {formatMYR(val.unitPrice)}
              </span>
            );
            if (indicatorColor && tp && tp > 0) {
              return (
                <Tooltip
                  title={
                    <div style={{ fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                        <span>Target Price:</span>
                        <b>{formatMYR(tp)}</b>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                        <span>Unit Price:</span>
                        <b>{formatMYR(val.unitPrice)}</b>
                      </div>
                      <div
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.3)",
                          marginTop: 4,
                          paddingTop: 4,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 16,
                        }}
                      >
                        <span>Variance:</span>
                        <b style={{ color: variance > 0 ? "#ffd666" : "#95de64" }}>
                          {variance > 0 ? "+" : ""}{formatMYR(variance)} ({deviation.toFixed(1)}%)
                        </b>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginTop: 2 }}>
                        <span>Status:</span>
                        <b>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              backgroundColor: indicatorColor,
                              marginRight: 4,
                            }}
                          />
                          {indicatorTitle}
                        </b>
                      </div>
                    </div>
                  }
                >
                  {priceEl}
                </Tooltip>
              );
            }
            return priceEl;
          },
        },
        {
          title: "Installation Charges (INR)",
          dataIndex: supplier.supplierID,
          key: `${supplier.supplierID}_install`,
          width: 160,
          align: "right" as const,
          render: (val: any) =>
            val ? formatMYR(val.totalInstallationCharges) : "-",
        },
        {
          title: "Discount (INR)",
          dataIndex: supplier.supplierID,
          key: `${supplier.supplierID}_discount`,
          width: 130,
          align: "right" as const,
          render: (val: any) => {
            if (!val) return "-";
            const disc = val.discountAmount || 0;
            if (disc === 0) return <span style={{ color: "#8c8c8c" }}>-</span>;
            let pct = val.discountPercent || 0;
            if (!pct && disc > 0) {
              const basePrice = val.supplyQty * val.unitPrice + val.totalInstallationCharges;
              if (basePrice > 0) pct = parseFloat(((disc / basePrice) * 100).toFixed(2));
            }
            return (
              <span style={{ color: "#d4380d", fontWeight: 600 }}>
                {formatMYR(disc)}{pct ? ` (${pct}%)` : ""}
              </span>
            );
          },
        },
        {
          title: "Total Price (INR)",
          dataIndex: supplier.supplierID,
          key: `${supplier.supplierID}_total`,
          width: 140,
          align: "right" as const,
          render: (val: any, record: any) => {
            if (!val) return "-";
            const lowest = lowestPricePerMaterial[record.tenderMaterialId];
            const isLowest = val.totalPrice === lowest;
            const isHighest =
              val.totalPrice === highestPricePerMaterial[record.tenderMaterialId] && !isLowest;
            const spreadAmt = lowest && lowest > 0 && !isLowest ? val.totalPrice - lowest : 0;
            const spreadPct = lowest && lowest > 0 && !isLowest ? (spreadAmt / lowest) * 100 : 0;
            const spreadColor = spreadPct > 20 ? "#ff4d4f" : spreadPct > 10 ? "#faad14" : "#d4a574";
            const spreadLabel = spreadPct > 20 ? "High" : spreadPct > 10 ? "Moderate" : "Low";

            const content = (
              <div style={{ textAlign: "right" }}>
                <span
                  style={{
                    color: isLowest ? "#52c41a" : isHighest ? "#ff4d4f" : "inherit",
                    fontWeight: isLowest || isHighest ? 600 : 400,
                  }}
                >
                  {formatMYR(val.totalPrice)}
                </span>
                {isLowest && (
                  <div style={{ fontSize: 10, color: "#52c41a", fontWeight: 500 }}>
                    Lowest
                  </div>
                )}
                {spreadPct > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: spreadColor,
                      fontWeight: 500,
                    }}
                  >
                    +{formatMYR(spreadAmt)} ({spreadPct.toFixed(1)}%)
                  </div>
                )}
              </div>
            );

            if (spreadPct > 0) {
              return (
                <Tooltip
                  title={
                    <div style={{ fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                        <span>Lowest Price:</span>
                        <b style={{ color: "#95de64" }}>{formatMYR(lowest)}</b>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                        <span>Total Price:</span>
                        <b>{formatMYR(val.totalPrice)}</b>
                      </div>
                      <div
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.3)",
                          marginTop: 4,
                          paddingTop: 4,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 16,
                        }}
                      >
                        <span>Spread:</span>
                        <b style={{ color: spreadColor }}>
                          +{formatMYR(spreadAmt)} ({spreadPct.toFixed(1)}%)
                        </b>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginTop: 2 }}>
                        <span>Status:</span>
                        <b>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              backgroundColor: spreadColor,
                              marginRight: 4,
                            }}
                          />
                          {spreadLabel}
                        </b>
                      </div>
                    </div>
                  }
                >
                  {content}
                </Tooltip>
              );
            }
            return content;
          },
        },
      ],
    });
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingTop: 10, gap: 8, flexWrap: "wrap" }}>
        {/* Site Hierarchy Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {hierarchyFilterOptions && (hierarchyFilterOptions.towers.length > 0 || hierarchyFilterOptions.villas.length > 0) && (
            <>
              <span style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>Filter by:</span>
              {isVillaFlow ? (
                <Select
                  placeholder="All Villas"
                  value={selectedTower}
                  onChange={(val) => onTowerChange?.(val)}
                  allowClear
                  style={{ minWidth: 160 }}
                  size="small"
                >
                  {hierarchyFilterOptions.villas.map((v) => (
                    <Select.Option key={v} value={v}>{v}</Select.Option>
                  ))}
                </Select>
              ) : (
                <>
                  {hierarchyFilterOptions.towers.length > 0 && (
                    <Select
                      placeholder="All Towers"
                      value={selectedTower}
                      onChange={(val) => onTowerChange?.(val)}
                      allowClear
                      style={{ minWidth: 150 }}
                      size="small"
                    >
                      {hierarchyFilterOptions.towers.map((t) => (
                        <Select.Option key={t} value={t}>{t}</Select.Option>
                      ))}
                    </Select>
                  )}
                  {availableFloors && availableFloors.length > 0 && (
                    <Select
                      placeholder="All Floors"
                      value={selectedFloor}
                      onChange={(val) => onFloorChange?.(val)}
                      allowClear
                      style={{ minWidth: 140 }}
                      size="small"
                    >
                      {availableFloors.map((f) => (
                        <Select.Option key={f} value={f}>Floor {f}</Select.Option>
                      ))}
                    </Select>
                  )}
                  {availableFlats && availableFlats.length > 0 && (
                    <Select
                      placeholder="All Flats"
                      value={selectedFlat}
                      onChange={(val) => onFlatChange?.(val)}
                      allowClear
                      style={{ minWidth: 140 }}
                      size="small"
                    >
                      {availableFlats.map((f) => (
                        <Select.Option key={f} value={f}>{f}</Select.Option>
                      ))}
                    </Select>
                  )}
                </>
              )}
              {(selectedTower || selectedFloor || selectedFlat) && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onTowerChange?.(undefined);
                    onFloorChange?.(undefined);
                    onFlatChange?.(undefined);
                  }}
                  style={{ fontSize: 12 }}
                >
                  Clear Filters
                </a>
              )}
              {totalMaterialCount !== undefined && (
                <span style={{ fontSize: 11, color: "#aaa" }}>
                  Showing {materialNames.length} of {totalMaterialCount} materials
                </span>
              )}
            </>
          )}
        </div>
        {/* Export / Import */}
        <div style={{ display: "flex", gap: 8 }}>
          <div
            className="btn btn-white btn-outline-light"
            onClick={handleExport}
            style={{ cursor: "pointer" }}
          >
            <Icon name="download-cloud" />
            <span>Export</span>
          </div>
          <Upload
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={handleImportTargetPrice}
          >
            <div
              className="btn btn-white btn-outline-light"
              style={{ cursor: "pointer" }}
            >
              <Icon name="upload-cloud" />
              <span>Import</span>
            </div>
          </Upload>
        </div>
      </div>
      <Table
        dataSource={dataSource}
        columns={columns}
        rowKey={(record: any) => record.tenderMaterialId ?? record.key}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: dataSource.length,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) setPageSize(size);
          },
        }}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
      />

      {/* Discount Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Apply Discount</span>
            <ul className="nk-block-tools g-3" style={{ display: "flex", listStyle: "none", margin: 0, padding: 0, gap: 8 }}>
              <li>
                <div
                  className="btn btn-white btn-outline-light"
                  onClick={handleDrawerExport}
                  style={{ cursor: "pointer" }}
                >
                  <Icon name="download-cloud" />
                  <span>Export</span>
                </div>
              </li>
              <li>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleDrawerImport}
                >
                  <div
                    className="btn btn-white btn-outline-light"
                    style={{ cursor: "pointer" }}
                  >
                    <Icon name="upload-cloud" />
                    <span>Import</span>
                  </div>
                </Upload>
              </li>
            </ul>
          </div>
        }
        placement="right"
        width="80%"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="actions clearfix">
            <ul>
              <li>
                <button
                  className="btn btn-primary"
                  onClick={handleApplyClick}
                  disabled={applyingDiscount[drawerSupplier?.supplierID]}
                >
                  {applyingDiscount[drawerSupplier?.supplierID] ? "Applying..." : "Apply Discount"}
                </button>
              </li>
              <li>
                <button
                  className="btn btn-dim btn-secondary"
                  onClick={() => setDrawerOpen(false)}
                >
                  Cancel
                </button>
              </li>
            </ul>
          </div>
        }
      >
        {/* Supplier info card */}
        {drawerSupplier && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
            padding: "14px 18px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: colors[drawerSupplierIndex % colors.length],
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getSupplierInitials(drawerSupplier?.supplierName || "")}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#222" }}>
                {drawerSupplier?.supplierName}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                ID: {drawerSupplier?.supplierID} &nbsp;|&nbsp; Payment Terms: {drawerSupplier?.paymentTerms || "N/A"}
              </div>
            </div>
            {(() => {
              const supplierSum = summaryData.find((s) => s.supplierID === drawerSupplier?.supplierID);
              if (!supplierSum) return null;
              return (
                <div style={{ display: "flex", gap: 24, fontSize: 12 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#888" }}>Sub Total</div>
                    <div style={{ fontWeight: 600 }}>{formatMYR(supplierSum.subTotal)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#888" }}>Taxable Amount</div>
                    <div style={{ fontWeight: 600 }}>{formatMYR(supplierSum.taxableAmount)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#888" }}>Tax</div>
                    <div style={{ fontWeight: 600 }}>{formatMYR(supplierSum.taxes)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#1890ff" }}>Gross Amount</div>
                    <div style={{ fontWeight: 700, color: "#1890ff", fontSize: 14 }}>{formatMYR(supplierSum.grossAmount)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        <Table
          dataSource={getDrawerMaterials()}
          rowKey={(record: any) => record.tenderMaterialId ?? record.key}
          columns={[
            {
              title: "Material Name",
              dataIndex: "materialName",
              key: "materialName",
              width: 250,
              render: (text: string, record: any) => {
                const parts = [
                  record.unitName || "",
                  record.floorNo ? `Floor: ${record.floorNo}` : "",
                  record.subUnitName ? `Flat: ${record.subUnitName}` : "",
                ].filter(Boolean);
                return (
                  <div>
                    <b>{text}</b>
                    {parts.length > 0 && (
                      <div style={{ fontSize: 11, color: "#1890ff", fontWeight: 500, marginTop: 2 }}>
                        {parts.join(" / ")}
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              title: "Usage",
              dataIndex: "usage",
              key: "usage",
              width: 100,
              ellipsis: true,
              render: (text: any) => renderUsageCell(text, 14),
            },
            {
              title: "Supply Qty",
              dataIndex: "supplyQty",
              key: "supplyQty",
              width: 100,
              align: "center" as const,
            },
            {
              title: "Unit Price (INR)",
              dataIndex: "unitPrice",
              key: "unitPrice",
              width: 150,
              align: "right" as const,
              render: (val: number) => formatMYR(val),
            },
            {
              title: "Installation (INR)",
              dataIndex: "totalInstallationCharges",
              key: "totalInstallationCharges",
              width: 150,
              align: "right" as const,
              render: (val: number) => formatMYR(val),
            },
            {
              title: "Total Price (INR)",
              dataIndex: "totalPrice",
              key: "totalPrice",
              width: 160,
              align: "right" as const,
              render: (val: number) => <b>{formatMYR(val)}</b>,
            },
            {
              title: "Discount (%)",
              key: "discount",
              width: 120,
              align: "center" as const,
              render: (_: any, record: any) => (
                <InputNumber
                  min={0}
                  max={100}
                  size="small"
                  style={{ width: "100%" }}
                  placeholder="Enter %"
                  value={lineDiscounts[record.tenderMaterialId] || undefined}
                  onChange={(val) =>
                    setLineDiscounts((prev) => ({
                      ...prev,
                      [record.tenderMaterialId]: val || 0,
                    }))
                  }
                />
              ),
            },
            {
              title: "Discount Amt (INR)",
              key: "discountAmt",
              width: 150,
              align: "right" as const,
              render: (_: any, record: any) => {
                const disc = lineDiscounts[record.tenderMaterialId] || 0;
                const amt = (record.totalPrice * disc) / 100;
                return disc > 0 ? (
                  <span style={{ color: "#fa541c", fontWeight: 600 }}>
                    - {formatMYR(amt)}
                  </span>
                ) : "-";
              },
            },
            {
              title: "Total After Discount (INR)",
              key: "totalAfterDiscount",
              width: 180,
              align: "right" as const,
              render: (_: any, record: any) => {
                const disc = lineDiscounts[record.tenderMaterialId] || 0;
                const amt = (record.totalPrice * disc) / 100;
                const afterDiscount = record.totalPrice - amt;
                return disc > 0 ? (
                  <b style={{ color: "#52c41a" }}>{formatMYR(afterDiscount)}</b>
                ) : <b>{formatMYR(record.totalPrice)}</b>;
              },
            },
          ]}
          pagination={{
            current: drawerCurrentPage,
            pageSize: drawerPageSize,
            total: getDrawerMaterials().length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setDrawerCurrentPage(page);
              if (size !== drawerPageSize) setDrawerPageSize(size);
            },
          }}
          scroll={{ x: "max-content" }}
          size="small"
          bordered
          summary={() => {
            const materials = getDrawerMaterials();
            let totalOriginal = 0;
            let totalDiscountAmt = 0;
            materials.forEach((mat: any) => {
              totalOriginal += mat.totalPrice;
              const disc = lineDiscounts[mat.tenderMaterialId] || 0;
              totalDiscountAmt += (mat.totalPrice * disc) / 100;
            });
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <b>Total</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <b>{formatMYR(totalOriginal)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align="right">
                    {totalDiscountAmt > 0 ? (
                      <span style={{ color: "#fa541c", fontWeight: 600 }}>
                        - {formatMYR(totalDiscountAmt)}
                      </span>
                    ) : "-"}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    {totalDiscountAmt > 0 ? (
                      <b style={{ color: "#52c41a" }}>
                        {formatMYR(totalOriginal - totalDiscountAmt)}
                      </b>
                    ) : <b>{formatMYR(totalOriginal)}</b>}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Drawer>

      {/* Revision Reason Modal */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 600 }}>Revision Reason</span>}
        open={reasonModalOpen}
        zIndex={1100}
        width={500}
        onCancel={() => {
          setReasonModalOpen(false);
          setRevisionReason("");
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              className="btn btn-dim btn-secondary"
              onClick={() => {
                setReasonModalOpen(false);
                setRevisionReason("");
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!revisionReason.trim() || applyingDiscount[drawerSupplier?.supplierID]}
              onClick={handleApplyAllDiscounts}
            >
              {applyingDiscount[drawerSupplier?.supplierID] ? "Applying..." : "Apply Discount"}
            </button>
          </div>
        }
      >
        <div style={{ marginBottom: 4 }}>
          <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 6 }}>
            Reason for Discount
          </label>
          <Input.TextArea
            rows={3}
            placeholder="Enter reason for discount"
            value={revisionReason}
            onChange={(e) => setRevisionReason(e.target.value)}
            style={{ borderRadius: 4 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ComparisonTable;
